import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { useMemo } from 'react';
import Error404 from '~/components/Error404';
import Error500 from '~/components/Error500';
import { TEST_CONSENT_MANAGER_ID } from '~/components/FakeDoorProvider/constants';
import { FakeDoorProvider } from '~/components/FakeDoorProvider/context';
import { MediaKit, getMKQueryKeyFn } from '~/hooks/api/media-kits/get';
import { processOgImages } from '~/metaDataUtils';
import { MediaKitPreview } from '~/screens/MediaKitPreview';
import { MediaKitFooter } from '~/screens/MediaKitPreview/Footer';
import { MadeWithFOAM } from '~/screens/MediaKitPreview/MadeWithFOAM';
import { mediaKitIds } from '~/screens/MediaKitPreview/mkMigrations';
import { Block } from '~/screens/PageBuilder/types';

interface Props {
  mediaKit: MediaKit | null;
  error: { message: string; code: number } | null;
}

const MediaKitSharedPage = ({ mediaKit, error }: Props) => {
  const hasFakeDoorManagerConcent = useMemo(() => {
    const managerId = mediaKit?.contact.user_id;
    if (!mediaKit || !managerId) return false;
    return TEST_CONSENT_MANAGER_ID.includes(managerId);
  }, [mediaKit]);

  if (error?.code === 404) {
    return <Error404 />;
  }

  if (error || !mediaKit) {
    return <Error500 />;
  }

  const { name, thumbnail, id: mediaKitId } = mediaKit;
  const pageUrl = `${process.env.NEXT_PUBLIC_APP_DOMAIN}/shared/media-kits/${mediaKitId}`;

  const TalentProfile = mediaKit.blocks.find(
    ({ component }) => component === 'TalentProfile',
  ) as Block<'TalentProfile'> | undefined;

  return (
    <FakeDoorProvider
      actions={[
        'Save for later',
        'Check Qualifications',
        'Analyze Content',
        'Suggest Similar',
        'Advanced Insights',
      ]}
      eventProperties={{
        creator_id: TalentProfile?.properties?.creator.id ?? 'N/A',
        creator_name: TalentProfile?.properties?.creator.fullName ?? 'N/A',
        media_kit_id: mediaKitId,
        media_kit_name: name,
        media_kit_owner_id: mediaKit.contact.user_id,
        type: 'media-kit',
      }}
      isEnabled={hasFakeDoorManagerConcent}
      pathname={pageUrl}
    >
      <div className="bg-[var(--media-kit-background-primary)]">
        <div className="tab:p-10">
          <NextSeo
            title={name}
            openGraph={{
              title: name,
              description: name,
              type: 'website',
              images: thumbnail ? processOgImages([thumbnail], 'mediaKit') : [],
              siteName: 'FOAM',
              url: pageUrl,
            }}
            twitter={{
              cardType: 'summary_large_image',
            }}
          />
          <div>
            <MediaKitPreview data={mediaKit} />
          </div>
          <MadeWithFOAM />
        </div>
        <MediaKitFooter agencyId={mediaKit.agency_id} />
      </div>
    </FakeDoorProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const queryClient = new QueryClient();

  try {
    const id = params?.id as string;

    if (mediaKitIds[id]) {
      // redirect to the new id with 301
      return {
        redirect: {
          destination: `/shared/media-kits/${mediaKitIds[id]}`,
          permanent: true,
        },
      };
    }

    const mediaKit = await queryClient.fetchQuery({
      ...getMKQueryKeyFn(params?.id as string),
      staleTime: 5 * 60 * 1000,
    });

    return {
      props: {
        isSSR: true,
        mediaKit,
        error: null,
      },
    };
  } catch (err: any) {
    const errorCode = err.message.includes('404') ? 404 : 500;

    return {
      props: {
        isSSR: true,
        mediaKit: null,
        error: {
          message: err.message,
          code: errorCode,
        },
      },
    };
  }
};

export default MediaKitSharedPage;
